import React, { useEffect, useMemo, useState } from 'react';
import { Session, SupabaseClient } from '@supabase/supabase-js';
import { SectionCard } from '../components/SectionCard';
import { Alert, showError, showInfo, showSuccess, showWarning } from '../lib/alerts';
import { WorkspaceOption } from '../types';

interface TeamPageProps {
  supabase: SupabaseClient;
  session: Session | null;
}

interface InviteRow {
  invite_code: string;
  created_at: string;
  is_active: boolean;
}

export default function TeamPage({ supabase, session }: TeamPageProps) {
  const [memberships, setMemberships] = useState<WorkspaceOption[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [members, setMembers] = useState<Array<{ userId: string; name: string; role: string }>>([]);
  const [invite, setInvite] = useState<InviteRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [inviteLoading, setInviteLoading] = useState(false);

  const selectedMembership = useMemo(
    () => (selectedProjectId === 'all' ? null : memberships.find((membership) => membership.projectId === selectedProjectId) || memberships[0] || null),
    [memberships, selectedProjectId]
  );

  useEffect(() => {
    if (!session) {
      return;
    }

    const userId = session.user.id;

    let cancelled = false;

    async function loadTeam() {
      setLoading(true);
      try {
        showInfo('Loading team details...');

        const { data: memberRows, error: memberError } = await supabase
          .from('project_members')
          .select('workspace_id, project_id, role')
          .eq('user_id', userId);

        if (memberError) throw memberError;

        const membershipRows = (memberRows || []) as Array<{ workspace_id: string; project_id: string; role: string }>;
        const projectIds = Array.from(new Set(membershipRows.map((row) => row.project_id).filter(Boolean)));
        const workspaceIds = Array.from(new Set(membershipRows.map((row) => row.workspace_id).filter(Boolean)));

        const projectRows: Array<{ id: string; name: string; workspace_id: string }> = [];
        const workspaceRows: Array<{ id: string; name: string }> = [];

        if (projectIds.length) {
          const { data, error } = await supabase.from('projects').select('id, name, workspace_id').in('id', projectIds);
          if (error) throw error;
          projectRows.push(...((data || []) as Array<{ id: string; name: string; workspace_id: string }>));
        }

        if (workspaceIds.length) {
          const { data, error } = await supabase.from('workspaces').select('id, name').in('id', workspaceIds);
          if (error) throw error;
          workspaceRows.push(...((data || []) as Array<{ id: string; name: string }>));
        }

        if (cancelled) return;

        const projectsById = new Map(projectRows.map((project) => [project.id, project] as const));
        const workspacesById = new Map(workspaceRows.map((workspace) => [workspace.id, workspace] as const));

        const nextMemberships = membershipRows.map((row) => ({
          workspaceId: row.workspace_id,
          workspaceName: workspacesById.get(row.workspace_id)?.name || 'Workspace',
          projectId: row.project_id,
          projectName: projectsById.get(row.project_id)?.name || 'Project',
          role: row.role,
        }));

        setMemberships(nextMemberships);
        const nextProjectId = selectedProjectId || (nextMemberships.length > 1 ? 'all' : nextMemberships[0]?.projectId || '');
        setSelectedProjectId(nextProjectId);

        const activeMembership = nextProjectId === 'all' ? null : nextMemberships.find(m => m.projectId === nextProjectId) || nextMemberships[0];

        if (activeMembership && nextProjectId && nextProjectId !== 'all') {
          const { data: inviteRows } = await supabase
            .from('workspace_invites')
            .select('invite_code, created_at, is_active')
            .eq('workspace_id', activeMembership.workspaceId)
            .eq('project_id', nextProjectId)
            .eq('is_active', true)
            .order('created_at', { ascending: false })
            .limit(1);

          setInvite((inviteRows || [])[0] || null);
        } else {
          setInvite(null);
        }

        await loadMembers(nextProjectId, workspaceIds);
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Failed to load team data');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    async function loadMembers(projectId: string, workspaceIds: string[]) {
      if (!projectId) {
        setMembers([]);
        return;
      }

      let memberRows: Array<{ user_id: string; role: string; project_id?: string }> = [];
      const usersById = new Map<string, { full_name: string | null; email: string }>();
      const projectIds: string[] = [];

      if (projectId === 'all') {
        if (!workspaceIds.length) {
          setMembers([]);
          return;
        }

        const { data, error } = await supabase
          .from('project_members')
          .select('user_id, role, project_id')
          .in('workspace_id', workspaceIds);

        if (error) throw error;

        const rawRows = (data || []) as Array<{ user_id: string; role: string; project_id: string }>;
        const memberMap = new Map<string, { user_id: string; role: string; project_id: string }>();
        const roleRank = (role: string) => (role === 'owner' ? 3 : role === 'admin' ? 2 : 1);

        for (const row of rawRows) {
          const existing = memberMap.get(row.user_id);
          if (!existing || roleRank(row.role) > roleRank(existing.role)) {
            memberMap.set(row.user_id, row);
          }
        }

        memberRows = Array.from(memberMap.values());
        projectIds.push(...Array.from(new Set(rawRows.map((row) => row.project_id).filter(Boolean))));
      } else {
        const { data, error } = await supabase
          .from('project_members')
          .select('user_id, role, project_id')
          .eq('project_id', projectId);

        if (error) throw error;

        memberRows = (data || []) as Array<{ user_id: string; role: string; project_id: string }>;
        projectIds.push(projectId);
      }

      const userIds = Array.from(new Set(memberRows.map((row) => row.user_id)));
      if (userIds.length && projectIds.length) {
        const rpcName = projectId === 'all' ? 'get_users_for_projects' : 'get_users_for_project';
        const rpcArgs = projectId === 'all'
          ? { project_ids: projectIds, user_ids: userIds }
          : { project_id: projectId, user_ids: userIds };

        const { data: userRows, error: userError } = await supabase.rpc(rpcName, rpcArgs as any);
        if (userError) {
          console.error('Error fetching users via RPC:', userError);
        } else if (userRows && Array.isArray(userRows)) {
          for (const user of userRows) {
            usersById.set(user.id, { full_name: user.full_name, email: user.email });
          }
        }
      }

      if (cancelled) return;

      setMembers(
        memberRows.map((row) => ({
          userId: row.user_id,
          name: usersById.get(row.user_id)?.full_name || usersById.get(row.user_id)?.email || `Member ${row.user_id.slice(0, 8)}`,
          role: row.role,
        }))
      );
    }

    void loadTeam();

    return () => {
      cancelled = true;
    };
  }, [session, selectedProjectId, supabase]);

  async function handleGenerateInvite() {
    if (!session || !selectedMembership) {
      showWarning('Select a workspace first');
      return;
    }

    setInviteLoading(true);
    try {
      const { data, error } = await supabase
        .from('workspace_invites')
        .insert({
          workspace_id: selectedMembership.workspaceId,
          project_id: selectedMembership.projectId,
          created_by: session.user.id,
        })
        .select('invite_code, created_at, is_active')
        .single();

      if (error) throw error;

      const nextInvite = data as InviteRow | null;
      setInvite(nextInvite);
      await Alert.fire({
        icon: 'success',
        title: 'Team Code Ready',
        html: `Share <b>${nextInvite?.invite_code || ''}</b> with teammates to join <b>${selectedMembership.workspaceName}</b>.`,
        confirmButtonText: 'Got it',
      });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create invite code');
    } finally {
      setInviteLoading(false);
    }
  }

  async function handleCopyInvite() {
    if (!invite?.invite_code) {
      showWarning('Generate a code first');
      return;
    }

    try {
      await navigator.clipboard.writeText(invite.invite_code);
      showSuccess('Invite code copied');
    } catch {
      showError('Could not copy invite code');
    }
  }

  return (
    <div className="dashboard-content dashboard-content--single">
      <div className="section-heading-row section-heading-row--spread">
        <div>
          <div className="section-kicker">TEAM ACTIVITY</div>
          <h2>Invite teammates and manage access.</h2>
        </div>
        <label className="topbar-select">
          <span>Project</span>
          <select 
            value={selectedProjectId} 
            onChange={(event) => setSelectedProjectId(event.target.value)}
            disabled={loading || memberships.length === 0}
          >
            {loading ? (
              <option value="">Loading projects...</option>
            ) : memberships.length === 0 ? (
              <option value="">No projects found</option>
            ) : (
              <>
                <option value="all">All teams</option>
                {memberships.map((membership) => (
                  <option key={membership.projectId} value={membership.projectId}>
                    {membership.workspaceName} / {membership.projectName}
                  </option>
                ))}
              </>
            )}
          </select>
        </label>
      </div>

      <div className="metrics-grid metrics-grid--top">
        <SectionCard title="Workspace" subtitle="Current team and project">
          <div className="team-summary">
            <div>
              <div className="team-summary__label">Workspace</div>
              <div className="team-summary__value">{selectedProjectId === 'all' ? 'All teams' : selectedMembership?.workspaceName || 'Workspace'}</div>
            </div>
            <div>
              <div className="team-summary__label">Project</div>
              <div className="team-summary__value">{selectedProjectId === 'all' ? 'All projects' : selectedMembership?.projectName || 'Project'}</div>
            </div>
            <div>
              <div className="team-summary__label">Role</div>
              <div className="team-summary__value">{selectedProjectId === 'all' ? 'Member' : selectedMembership?.role || 'owner'}</div>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Team Code" subtitle="Generate a unique invite code for your team">
          <div className="team-invite">
            <div className="team-invite__code">{invite?.invite_code || 'No active code yet'}</div>
            <div className="team-invite__meta">{invite ? 'Anyone with this code can join your project.' : 'Create one to share with teammates.'}</div>
            <div className="team-invite__actions">
              <button type="button" className="button button--solid button--mono" onClick={handleGenerateInvite} disabled={inviteLoading || loading || !selectedMembership || selectedProjectId === 'all'}>
                {inviteLoading ? 'Generating...' : 'Generate Code'}
              </button>
              <button type="button" className="button button--ghost button--mono" onClick={handleCopyInvite} disabled={!invite?.invite_code || inviteLoading}>
                Copy Code
              </button>
            </div>
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Members" subtitle="People who can access this project">
        <div className="member-list">
          {(() => {
            const teamLeads = members.filter(member => member.role === 'owner' || member.role === 'admin');
            const regularMembers = members.filter(member => member.role === 'member');
            
            return (
              <>
                {teamLeads.length > 0 && (
                  <div className="member-group">
                    <h4 className="member-group-title">Team Leads</h4>
                    {teamLeads.map((member) => (
                      <div className="member-row member-row--lead" key={member.userId}>
                        <div>
                          <div className="member-row__name">{member.name}</div>
                          <div className="member-row__id">{member.userId}</div>
                        </div>
                        <div className="role-pill role-pill--lead">{member.role}</div>
                      </div>
                    ))}
                  </div>
                )}
                
                {regularMembers.length > 0 && (
                  <div className="member-group">
                    <h4 className="member-group-title">Team Members</h4>
                    {regularMembers.map((member) => (
                      <div className="member-row" key={member.userId}>
                        <div>
                          <div className="member-row__name">{member.name}</div>
                          <div className="member-row__id">{member.userId}</div>
                          {teamLeads.length > 0 && (
                            <div className="member-row__lead">
                              Reports to: {teamLeads.map(lead => lead.name).join(', ')}
                            </div>
                          )}
                        </div>
                        <div className="role-pill">{member.role}</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </SectionCard>
    </div>
  );
}
